/**
 * Subcontractor Assignments Page
 * Shows the subcontractor's own assignments with filtering
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  Calendar,
  MapPin,
  User,
  Leaf,
  Loader2,
  Search,
  ClipboardList,
  CheckCircle,
  Clock,
  AlertCircle,
  Wallet,
  Phone,
  X,
  FileSpreadsheet
} from 'lucide-react'
import type { NavigateFunction } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { sv } from 'date-fns/locale'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth'
import { getTgWorkStatusName, getTgWorkStatusColor } from '../../services/statusSystemV2'
import type { TgWorkStatus } from '../../types/statusTypes'
import { usePageTracking } from '../../hooks/usePageTracking'
import { ExportInvoiceModal } from '../../components/ExportInvoiceModal'

interface Coworker {
  id: string
  name: string
  phone: string | null
  color: string | null
}

interface Assignment {
  id: string
  assignment_number: number | null
  title: string
  description: string | null
  start_date: string
  start_time: string | null
  end_time: string | null
  status: string
  customer_name: string
  location: string | null
  tg_status: string | null
  tg_invoice_status: string | null  // Admin-controlled, used for filtering
  tg_personal_invoice_marked: boolean  // TG's personal tracking, does NOT affect workflow
  coworkers: Coworker[]  // Other TGs on the same assignment
}

// Statuses visible to subcontractors (V2 statuses)
const VISIBLE_STATUSES = [
  'planerad',      // Upcoming confirmed assignments
  'pagaende',      // In progress - needs reporting
  'granskning',    // Waiting for admin review
  'attesterad',    // Approved
  'slutford'       // Completed
]

type FilterType = 'all' | 'upcoming' | 'to_report' | 'needs_supplement' | 'in_review' | 'ready_to_invoice'

const ITEMS_PER_PAGE = 5

// Assignment card component
interface AssignmentCardProps {
  assignment: Assignment
  navigate: NavigateFunction
  showInvoiceCheckbox?: boolean
  onPersonalInvoiceToggle?: (assignmentId: string, marked: boolean) => void
  onCoworkerClick?: (coworker: Coworker) => void
}

function AssignmentCard({ assignment, navigate, showInvoiceCheckbox, onPersonalInvoiceToggle, onCoworkerClick }: AssignmentCardProps) {
  // Use TG's individual status instead of assignment status
  const statusColor = getTgWorkStatusColor(assignment.tg_status as TgWorkStatus)
  const statusName = getTgWorkStatusName(assignment.tg_status as TgWorkStatus)

  const handleInvoiceToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onPersonalInvoiceToggle) {
      onPersonalInvoiceToggle(assignment.id, !assignment.tg_personal_invoice_marked)
    }
  }

  return (
    <div
      onClick={() => navigate(`/subcontractor/assignments/${assignment.id}`)}
      className="w-full text-left bg-white dark:bg-nature-800 rounded-lg border border-nature-200 dark:border-nature-700 p-4 hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600 transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {assignment.assignment_number && (
            <span className="text-sm font-mono font-medium text-primary-600 dark:text-primary-400 shrink-0">
              #{assignment.assignment_number}
            </span>
          )}
          <h3 className="font-medium text-nature-900 dark:text-nature-100 line-clamp-1">
            {assignment.title}
          </h3>
        </div>
        <span
          className="px-2 py-0.5 text-xs rounded-full whitespace-nowrap flex-shrink-0"
          style={{
            backgroundColor: `${statusColor}20`,
            color: statusColor
          }}
        >
          {statusName}
        </span>
      </div>

      <div className="space-y-1.5 text-sm text-nature-600 dark:text-nature-400">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 flex-shrink-0" />
          <span>
            {format(parseISO(assignment.start_date), 'yyyy-MM-dd', { locale: sv })}
            {assignment.start_time && ` kl ${assignment.start_time.slice(0, 5)}`}
            {assignment.end_time && ` - ${assignment.end_time.slice(0, 5)}`}
          </span>
        </div>

        {assignment.customer_name && (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{assignment.customer_name}</span>
          </div>
        )}

        {assignment.location && (
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(assignment.location)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="truncate text-primary-600 dark:text-primary-400 hover:underline"
            >
              {assignment.location}
            </a>
          </div>
        )}

        {/* Coworkers */}
        {assignment.coworkers.length > 0 && (
          <div className="flex items-center gap-2">
            <Leaf className="h-4 w-4 flex-shrink-0" />
            <div className="flex flex-wrap gap-1">
              {assignment.coworkers.map(coworker => (
                <button
                  key={coworker.id}
                  onClick={(e) => {
                    e.stopPropagation()
                    onCoworkerClick?.(coworker)
                  }}
                  className="px-2 py-0.5 text-xs rounded-full hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: coworker.color ? `${coworker.color}20` : '#6B728020',
                    color: coworker.color || '#6B7280'
                  }}
                >
                  {coworker.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Personal invoice tracking checkbox - does NOT affect workflow */}
      {showInvoiceCheckbox && (
        <div className="mt-3 pt-3 border-t border-nature-200 dark:border-nature-700">
          <label
            onClick={handleInvoiceToggle}
            className={`flex items-center gap-2 cursor-pointer ${assignment.tg_personal_invoice_marked ? 'text-green-600 dark:text-green-400' : 'text-nature-500 dark:text-nature-400'}`}
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
              assignment.tg_personal_invoice_marked
                ? 'bg-green-600 border-green-600 dark:bg-green-500 dark:border-green-500'
                : 'border-nature-300 dark:border-nature-600 hover:border-green-400'
            }`}>
              {assignment.tg_personal_invoice_marked && <CheckCircle className="h-3 w-3 text-white" />}
            </div>
            <span className="text-sm">
              {assignment.tg_personal_invoice_marked ? 'Faktura skickad' : 'Markera som fakturerad'}
            </span>
          </label>
        </div>
      )}
    </div>
  )
}

export function SubcontractorAssignments() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<FilterType>('all')
  const [showAllUpcoming, setShowAllUpcoming] = useState(false)
  const [showAllNeedsSupplement, setShowAllNeedsSupplement] = useState(false)
  const [showAllInReview, setShowAllInReview] = useState(false)
  const [showAllReadyToInvoice, setShowAllReadyToInvoice] = useState(false)
  const [selectedCoworker, setSelectedCoworker] = useState<Coworker | null>(null)
  const [showExportModal, setShowExportModal] = useState(false)

  // Track page view
  usePageTracking('assignments')

  // Fetch subcontractor ID
  const { data: subcontractor } = useQuery({
    queryKey: ['my-subcontractor', user?.id],
    queryFn: async () => {
      if (!user?.id) return null
      const { data, error } = await supabase
        .from('subcontractors')
        .select('id, name')
        .eq('user_id', user.id)
        .single()
      if (error) throw error
      return data
    },
    enabled: !!user?.id
  })

  // Fetch all assignments for the subcontractor
  const { data: assignments = [], isLoading } = useQuery<Assignment[]>({
    queryKey: ['subcontractor-assignments', subcontractor?.id],
    queryFn: async () => {
      if (!subcontractor?.id) return []

      // First, get the assignments where the user is assigned
      const { data, error } = await supabase
        .from('assignments')
        .select(`
          id,
          assignment_number,
          title,
          description,
          start_date,
          start_time,
          end_time,
          status,
          location,
          customers (full_name, hide_details_from_tg),
          assignment_subcontractors!inner (subcontractor_id, tg_status, tg_invoice_status, tg_personal_invoice_marked)
        `)
        .eq('assignment_subcontractors.subcontractor_id', subcontractor.id)
        .neq('assignment_subcontractors.tg_status', 'avsagt')
        .neq('assignment_subcontractors.tg_status', 'preliminar_tilldelad')
        .neq('assignment_subcontractors.tg_invoice_status', 'faktura_mottagen') // Exkludera avslutade för att minska dataladdning
        .in('status', VISIBLE_STATUSES)
        .order('start_date', { ascending: true })
        .order('start_time', { ascending: true })

      if (error) throw error

      const assignmentIds = (data || []).map(a => a.id)

      // Then, fetch all other subcontractors on these assignments (exclude declined)
      const coworkersByAssignment: Record<string, Coworker[]> = {}
      if (assignmentIds.length > 0) {
        const { data: coworkerData } = await supabase
          .from('assignment_subcontractors')
          .select(`
            assignment_id,
            subcontractors (id, name, phone, color)
          `)
          .in('assignment_id', assignmentIds)
          .neq('subcontractor_id', subcontractor.id)
          .neq('tg_status', 'avsagt')

        // Group coworkers by assignment
        for (const row of coworkerData || []) {
          const assignmentId = row.assignment_id
          const sub = row.subcontractors as any
          if (sub) {
            if (!coworkersByAssignment[assignmentId]) {
              coworkersByAssignment[assignmentId] = []
            }
            coworkersByAssignment[assignmentId].push({
              id: sub.id,
              name: sub.name,
              phone: sub.phone,
              color: sub.color
            })
          }
        }
      }

      return (data || []).map(a => {
        const tgData = (a.assignment_subcontractors as any)?.[0] || {}
        const customer = a.customers as any
        const hideDetails = customer?.hide_details_from_tg
        return {
          id: a.id,
          assignment_number: a.assignment_number,
          title: a.title,
          description: a.description,
          start_date: a.start_date,
          start_time: a.start_time,
          end_time: a.end_time,
          status: a.status,
          customer_name: hideDetails ? '' : (customer?.full_name || 'Okänd kund'),
          location: typeof a.location === 'string' ? a.location : (a.location as any)?.address || null,
          tg_status: tgData.tg_status || null,
          tg_invoice_status: tgData.tg_invoice_status || null,
          tg_personal_invoice_marked: tgData.tg_personal_invoice_marked || false,
          coworkers: coworkersByAssignment[a.id] || []
        }
      })
    },
    enabled: !!subcontractor?.id
  })

  // Handle personal invoice marking toggle (TG's own tracking, does NOT affect workflow)
  const handlePersonalInvoiceToggle = async (assignmentId: string, marked: boolean) => {
    if (!subcontractor?.id) return

    try {
      const { error } = await supabase
        .from('assignment_subcontractors')
        .update({ tg_personal_invoice_marked: marked })
        .eq('assignment_id', assignmentId)
        .eq('subcontractor_id', subcontractor.id)

      if (error) throw error

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['subcontractor-assignments'] })

      toast.success(marked ? 'Markerad som fakturerad' : 'Markering borttagen')
    } catch (err) {
      console.error('Error updating invoice status:', err)
      toast.error('Kunde inte uppdatera fakturastatus')
    }
  }

  // Filter and search assignments
  const filteredAssignments = useMemo(() => {
    return assignments.filter(a => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const matchesSearch =
          a.title.toLowerCase().includes(query) ||
          a.customer_name.toLowerCase().includes(query) ||
          (a.location && a.location.toLowerCase().includes(query)) ||
          (a.assignment_number && a.assignment_number.toString().includes(query))
        if (!matchesSearch) return false
      }

      // Status filter - based on TG's individual status, not assignment status
      switch (statusFilter) {
        case 'upcoming':
          // Upcoming: TG is tilldelad (assignment not yet done)
          return a.tg_status === 'tilldelad'
        case 'to_report':
          // Needs to report: TG status is ej_rapporterad (assignment done, needs report)
          return a.tg_status === 'ej_rapporterad'
        case 'needs_supplement':
          return a.tg_status === 'komplettering'
        case 'in_review':
          // In review: TG has reported and is waiting for approval
          return a.tg_status === 'rapporterad'
        case 'ready_to_invoice':
          // Ready to invoice: TG is approved
          return a.tg_status === 'godkand'
        default:
          return true
      }
    })
  }, [assignments, searchQuery, statusFilter])

  // Group assignments by category
  const { upcomingList, needsSupplementList, toReportList, inReviewList, readyToInvoiceList } = useMemo(() => {
    const upcoming: Assignment[] = []
    const needsSupplement: Assignment[] = []
    const toReport: Assignment[] = []
    const inReview: Assignment[] = []
    const readyToInvoice: Assignment[] = []

    filteredAssignments.forEach(a => {
      // Behöver komplettering - highest priority
      if (a.tg_status === 'komplettering') {
        needsSupplement.push(a)
      }
      // Redo för fakturering - TG is approved
      else if (a.tg_status === 'godkand') {
        readyToInvoice.push(a)
      }
      // Under granskning - TG has reported and is waiting for approval
      else if (a.tg_status === 'rapporterad') {
        inReview.push(a)
      }
      // Att rapportera - assignment done, needs report
      else if (a.tg_status === 'ej_rapporterad') {
        toReport.push(a)
      }
      // Kommande - TG is tilldelad (assignment not yet done)
      else if (a.tg_status === 'tilldelad') {
        upcoming.push(a)
      }
    })

    // Sort upcoming by date ascending (closest first)
    upcoming.sort((a, b) => a.start_date.localeCompare(b.start_date) || (a.start_time || '').localeCompare(b.start_time || ''))
    // Sort needs supplement by date ascending (oldest first - most urgent)
    needsSupplement.sort((a, b) => a.start_date.localeCompare(b.start_date) || (a.start_time || '').localeCompare(b.start_time || ''))
    // Sort to report by date ascending (oldest first - most urgent)
    toReport.sort((a, b) => a.start_date.localeCompare(b.start_date) || (a.start_time || '').localeCompare(b.start_time || ''))
    // Sort in review by date descending (most recent first)
    inReview.sort((a, b) => b.start_date.localeCompare(a.start_date) || (b.start_time || '').localeCompare(a.start_time || ''))
    // Sort ready to invoice by date descending (most recent first)
    readyToInvoice.sort((a, b) => b.start_date.localeCompare(a.start_date) || (b.start_time || '').localeCompare(a.start_time || ''))

    return { upcomingList: upcoming, needsSupplementList: needsSupplement, toReportList: toReport, inReviewList: inReview, readyToInvoiceList: readyToInvoice }
  }, [filteredAssignments])

  // Count for badges - based on TG's individual status
  const counts = useMemo(() => {
    return {
      all: assignments.length,
      upcoming: assignments.filter(a =>
        // Kommande: TG is tilldelad (assignment not yet done)
        a.tg_status === 'tilldelad'
      ).length,
      needs_supplement: assignments.filter(a =>
        a.tg_status === 'komplettering'
      ).length,
      to_report: assignments.filter(a =>
        // Att rapportera: assignment done, needs report
        a.tg_status === 'ej_rapporterad'
      ).length,
      in_review: assignments.filter(a =>
        // TG has reported and is waiting for approval
        a.tg_status === 'rapporterad'
      ).length,
      ready_to_invoice: assignments.filter(a =>
        // TG is approved and not personally marked as invoiced
        a.tg_status === 'godkand' && !a.tg_personal_invoice_marked
      ).length
    }
  }, [assignments])

  if (isLoading && !subcontractor) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100">
            Mina Uppdrag
          </h1>
          <p className="text-nature-600 dark:text-nature-400">
            {subcontractor?.name ? `Uppdrag för ${subcontractor.name}` : 'Dina tilldelade uppdrag'}
          </p>
        </div>

        {/* Export button */}
        {counts.ready_to_invoice > 0 && (
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors self-start sm:self-auto"
          >
            <FileSpreadsheet className="h-4 w-4" />
            <span>Exportera faktureringsunderlag</span>
            <span className="px-1.5 py-0.5 text-xs bg-white/20 rounded">
              {counts.ready_to_invoice}
            </span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nature-400" />
          <input
            type="text"
            placeholder="Sök..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-800 text-nature-900 dark:text-nature-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Status filter */}
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'all' as FilterType, label: 'Alla', count: counts.all },
            { key: 'needs_supplement' as FilterType, label: 'Komplettera', count: counts.needs_supplement },
            { key: 'to_report' as FilterType, label: 'Rapportera', count: counts.to_report },
            { key: 'upcoming' as FilterType, label: 'Kommande', count: counts.upcoming },
            { key: 'in_review' as FilterType, label: 'Granskning', count: counts.in_review },
            { key: 'ready_to_invoice' as FilterType, label: 'Fakturera', count: counts.ready_to_invoice }
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-2 ${
                statusFilter === f.key
                  ? 'bg-primary-600 text-white'
                  : 'bg-nature-100 dark:bg-nature-700 text-nature-700 dark:text-nature-300 hover:bg-nature-200 dark:hover:bg-nature-600'
              }`}
            >
              {f.label}
              {f.count > 0 && (
                <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                  statusFilter === f.key
                    ? 'bg-white/20'
                    : 'bg-nature-200 dark:bg-nature-600'
                }`}>
                  {f.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Assignments list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : filteredAssignments.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-nature-800 rounded-lg border border-nature-200 dark:border-nature-700">
          <ClipboardList className="mx-auto h-12 w-12 text-nature-400" />
          <h3 className="mt-4 text-lg font-medium text-nature-900 dark:text-nature-100">
            {searchQuery || statusFilter !== 'all' ? 'Inga uppdrag matchar filtret' : 'Inga uppdrag'}
          </h3>
          <p className="mt-2 text-nature-500 dark:text-nature-400">
            {searchQuery || statusFilter !== 'all'
              ? 'Prova att ändra sökning eller filter'
              : 'Du har inga aktiva uppdrag just nu'}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Behöver komplettering section - HIGHEST priority */}
          {needsSupplementList.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4 flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Behöver komplettering ({needsSupplementList.length})
              </h2>
              <p className="text-sm text-nature-500 dark:text-nature-400 mb-3">
                Admin har begärt komplettering. Uppdatera din rapport.
              </p>
              <div className="space-y-3">
                {(showAllNeedsSupplement ? needsSupplementList : needsSupplementList.slice(0, ITEMS_PER_PAGE)).map(assignment => (
                  <AssignmentCard key={assignment.id} assignment={assignment} navigate={navigate} onCoworkerClick={setSelectedCoworker} />
                ))}
              </div>
              {needsSupplementList.length > ITEMS_PER_PAGE && (
                <button
                  onClick={() => setShowAllNeedsSupplement(!showAllNeedsSupplement)}
                  className="mt-3 w-full py-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center justify-center gap-1"
                >
                  {showAllNeedsSupplement ? 'Visa färre' : `Visa alla (${needsSupplementList.length})`}
                </button>
              )}
            </div>
          )}

          {/* Att rapportera section */}
          {toReportList.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-amber-600 dark:text-amber-400 mb-4 flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                Att rapportera ({toReportList.length})
              </h2>
              <div className="space-y-3">
                {toReportList.map(assignment => (
                  <AssignmentCard key={assignment.id} assignment={assignment} navigate={navigate} onCoworkerClick={setSelectedCoworker} />
                ))}
              </div>
            </div>
          )}

          {/* Kommande section */}
          {upcomingList.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100 mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary-600" />
                Kommande ({upcomingList.length})
              </h2>
              <div className="space-y-3">
                {(showAllUpcoming ? upcomingList : upcomingList.slice(0, ITEMS_PER_PAGE)).map(assignment => (
                  <AssignmentCard key={assignment.id} assignment={assignment} navigate={navigate} onCoworkerClick={setSelectedCoworker} />
                ))}
              </div>
              {upcomingList.length > ITEMS_PER_PAGE && (
                <button
                  onClick={() => setShowAllUpcoming(!showAllUpcoming)}
                  className="mt-3 w-full py-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center justify-center gap-1"
                >
                  {showAllUpcoming ? 'Visa färre' : `Visa alla (${upcomingList.length})`}
                </button>
              )}
            </div>
          )}

          {/* Under granskning section */}
          {inReviewList.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-blue-600 dark:text-blue-400 mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Under granskning ({inReviewList.length})
              </h2>
              <p className="text-sm text-nature-500 dark:text-nature-400 mb-3">
                Rapporten är inskickad och väntar på godkännande. Du kan fakturera när den är godkänd.
              </p>
              <div className="space-y-3">
                {(showAllInReview ? inReviewList : inReviewList.slice(0, ITEMS_PER_PAGE)).map(assignment => (
                  <AssignmentCard key={assignment.id} assignment={assignment} navigate={navigate} onCoworkerClick={setSelectedCoworker} />
                ))}
              </div>
              {inReviewList.length > ITEMS_PER_PAGE && (
                <button
                  onClick={() => setShowAllInReview(!showAllInReview)}
                  className="mt-3 w-full py-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center justify-center gap-1"
                >
                  {showAllInReview ? 'Visa färre' : `Visa alla (${inReviewList.length})`}
                </button>
              )}
            </div>
          )}

          {/* Redo för fakturering section */}
          {readyToInvoiceList.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-green-600 dark:text-green-400 mb-4 flex items-center gap-2">
                <Wallet className="h-5 w-5" />
                Redo för fakturering ({readyToInvoiceList.length})
              </h2>
              <p className="text-sm text-nature-500 dark:text-nature-400 mb-3">
                Godkända uppdrag som du kan fakturera.
              </p>
              <div className="space-y-3">
                {(showAllReadyToInvoice ? readyToInvoiceList : readyToInvoiceList.slice(0, ITEMS_PER_PAGE)).map(assignment => (
                  <AssignmentCard key={assignment.id} assignment={assignment} navigate={navigate} showInvoiceCheckbox onPersonalInvoiceToggle={handlePersonalInvoiceToggle} onCoworkerClick={setSelectedCoworker} />
                ))}
              </div>
              {readyToInvoiceList.length > ITEMS_PER_PAGE && (
                <button
                  onClick={() => setShowAllReadyToInvoice(!showAllReadyToInvoice)}
                  className="mt-3 w-full py-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center justify-center gap-1"
                >
                  {showAllReadyToInvoice ? 'Visa färre' : `Visa alla (${readyToInvoiceList.length})`}
                </button>
              )}
            </div>
          )}

        </div>
      )}

      {/* Coworker contact popup */}
      {selectedCoworker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedCoworker(null)}>
          <div
            className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                  style={{ backgroundColor: selectedCoworker.color || '#6B7280' }}
                >
                  {selectedCoworker.name.charAt(0)}
                </div>
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                  {selectedCoworker.name}
                </h3>
              </div>
              <button
                onClick={() => setSelectedCoworker(null)}
                className="p-1 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-nature-500" />
              </button>
            </div>

            {selectedCoworker.phone ? (
              <a
                href={`tel:${selectedCoworker.phone}`}
                className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
              >
                <Phone className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <span className="text-primary-600 dark:text-primary-400 font-medium">
                  {selectedCoworker.phone}
                </span>
              </a>
            ) : (
              <p className="text-nature-500 dark:text-nature-400 text-sm">
                Inget telefonnummer registrerat
              </p>
            )}
          </div>
        </div>
      )}

      {/* Export Invoice Modal */}
      <ExportInvoiceModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        assignments={readyToInvoiceList}
        subcontractorId={subcontractor?.id || ''}
        onExportComplete={() => queryClient.invalidateQueries({ queryKey: ['subcontractor-assignments'] })}
      />
    </div>
  )
}
